import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { PagaebleResult, SearchVendedorDTO, VendedorDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    editSellerLocation,
    errorMessage,
    initials,
    labelize,
    pageWindow,
    personName,
    sellerContact,
} from '../common/seller-common'

export const PAGE_SIZES = [20, 50, 100]
const MIN_SEARCH_LENGTH = 3
const SEARCH_DELAY_MS = 400
const LOAD_ERROR = 'No se pudo cargar el listado de vendedores.'

export interface SellerRow {
    id: number
    name: string
    initials: string
    sexo: string | null
    rfc: string
    email: string
    phone: string
}

const toRow = (vendedor: SearchVendedorDTO): SellerRow => {
    const name = personName(vendedor)
    return {
        id: vendedor.id,
        name,
        initials: initials(name),
        sexo: labelize(vendedor.sexo),
        ...sellerContact(vendedor),
    }
}

const isSearchable = (value: string) => value.length === 0 || value.length >= MIN_SEARCH_LENGTH

export const useSellerListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const textTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [nombre, setNombre] = useState('')
    const [queryNombre, setQueryNombre] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<SearchVendedorDTO[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [detailId, setDetailId] = useState<number | null>(() => (location.state as { detailId?: number } | null)?.detailId || null)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const nombreParam = queryNombre.length >= MIN_SEARCH_LENGTH ? `&nombres[cn]=${encodeURIComponent(queryNombre)}` : ''
                const response = await APIInvoke.invokeGET(`/vendedores?page=${page}&size=${pageSize}${nombreParam}&order=nombres asc`)
                const payload: WrapperResponse<PagaebleResult<SearchVendedorDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setTotalItems(payload.body.totalItems || 0)
                setTotalPages(payload.body.totalPages || 0)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [page, pageSize, queryNombre, reloadToken])

    useEffect(() => () => clearTimeout(textTimer.current), [])

    const applySearch = (value: string) => {
        const trimmed = value.trim()
        if (!isSearchable(trimmed)) return
        setPage(1)
        setQueryNombre(trimmed)
    }

    const changeSearch = (value: string) => {
        setNombre(value)
        clearTimeout(textTimer.current)
        textTimer.current = setTimeout(() => applySearch(value), SEARCH_DELAY_MS)
    }

    const submitSearch = () => {
        clearTimeout(textTimer.current)
        applySearch(nombre)
    }

    const clearSearch = () => {
        setNombre('')
        clearTimeout(textTimer.current)
        setPage(1)
        setQueryNombre('')
    }

    const changePageSize = (value: number) => {
        setPageSize(Number(value))
        setPage(1)
    }

    const deleted = (vendedor?: VendedorDTO | null) => {
        setItems((current) => current.filter((item) => item.id !== vendedor?.id))
        setTotalItems((current) => Math.max(0, current - 1))
        setDetailId(null)
    }

    const rangeFrom = totalItems === 0 ? 0 : ((page - 1) * pageSize) + 1

    return {
        nombre,
        page,
        pageSize,
        loading,
        initialLoadDone,
        error,
        rows: items.map(toRow),
        totalItems,
        totalPages,
        pages: totalPages > 0 ? pageWindow(page, totalPages) : [],
        rangeFrom,
        rangeTo: totalItems === 0 ? 0 : rangeFrom + items.length - 1,
        searchHint: !isSearchable(nombre.trim()),
        detailId,
        changeSearch,
        submitSearch,
        clearSearch,
        setPage,
        changePageSize,
        reload: () => setReloadToken((token) => token + 1),
        openVendedor: (id: number) => setDetailId(id),
        closeVendedor: () => setDetailId(null),
        editVendedor: (id: number) => history.push(editSellerLocation(id)),
        deleted,
    }
}
